import React from 'react';
import {
  ChartBarIcon,
  UserIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  AcademicCapIcon,
  ClipboardDocumentIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  EyeIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

const TargetIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
  </svg>
);

const iconMap = {
  ChartBarIcon,
  UserIcon,
  SearchIcon: MagnifyingGlassIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  AcademicCapIcon,
  ClipboardDocumentIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  EyeIcon,
  TargetIcon,
};

const GettingStartedStepCard = ({
  activity,
  completed = false,
  locked = false,
  lockedLabel = 'Locked',
  stepNumber,
}) => {
  const IconComponent = iconMap[activity.icon] || DocumentTextIcon;

  const handleActivate = (e) => {
    if (locked) { e?.preventDefault?.(); return; }

    try {
      if (window.ahoy?.track) {
        window.ahoy.track(`getting_started_${activity.id}_clicked`);
      }
    } catch (_) {}

    // Render hint: an activity may intercept its own activation instead of
    // navigating — e.g. the RJ marketplace visibility card opens an inline
    // sheet in place. Mirrors the `videoTask` hint QuickstartBlock uses.
    if (typeof activity.onActivate === 'function') {
      e?.preventDefault?.();
      activity.onActivate();
      return;
    }

    if (activity.id === 'find_my_gaps') {
      e?.preventDefault?.();
      window.location.href = '/succession/plans/comprehensive-succession-plan-v2';
      return;
    }

    const path = activity.path;
    if (!path) return;
    if (activity.id === 'consultation') {
      try { window.open(path, '_blank', 'noopener'); } catch (_) { window.location.href = path; }
    } else {
      window.location.href = path;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate(e);
    }
  };

  const rowClasses = locked
    ? 'bg-succession-bg-tertiary border-succession-border opacity-60 cursor-not-allowed'
    : completed
      ? 'bg-succession-success-bg border-succession-success'
      : 'bg-succession-bg-tertiary border-succession-border hover:border-succession-success hover:bg-succession-hover-bg cursor-pointer';

  // Step-number / status circle (left). Lock takes precedence over completion so a
  // locked item always shows the lock icon, even if the user previously touched it.
  const statusCircle = (() => {
    if (locked) {
      return (
        <div className="w-8 h-8 rounded-full bg-succession-bg-secondary border-2 border-succession-border flex items-center justify-center">
          <LockClosedIcon className="w-4 h-4 text-succession-text-muted" aria-hidden="true" />
        </div>
      );
    }
    if (completed) {
      return (
        <div className="w-8 h-8 rounded-full bg-succession-success/15 text-succession-success flex items-center justify-center">
          <CheckCircleSolidIcon className="w-5 h-5" aria-hidden="true" />
        </div>
      );
    }
    return (
      <div className="w-8 h-8 rounded-full bg-succession-primary/10 text-succession-primary border-2 border-succession-primary/20 flex items-center justify-center text-sm font-semibold">
        {stepNumber}
      </div>
    );
  })();

  // Activity icon tile (between status and content)
  const iconTileClasses = locked
    ? 'bg-succession-bg-secondary text-succession-text-muted'
    : completed
      ? 'bg-succession-success/15 text-succession-success'
      : `${activity.bgColor} ${activity.iconColor}`;

  return (
    <div
      className={`${rowClasses} relative flex items-center gap-4 p-4 rounded-lg border transition-all`}
      role="button"
      tabIndex={locked ? -1 : 0}
      aria-disabled={locked || undefined}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      <div className="flex-shrink-0">{statusCircle}</div>

      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${iconTileClasses}`}>
        <IconComponent className="w-5 h-5" aria-hidden="true" />
      </div>

      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-succession-text-primary leading-tight truncate">{activity.title}</h4>
        <p className="mt-0.5 text-sm text-succession-text-secondary leading-snug line-clamp-1">
          {activity.shortDescription || activity.description}
        </p>
      </div>

      {locked && (
        <div className="flex-shrink-0">
          <span className="px-2.5 py-1 rounded-full bg-succession-bg-secondary text-xs font-medium text-succession-text-muted">
            {lockedLabel}
          </span>
        </div>
      )}
    </div>
  );
};

export default GettingStartedStepCard;
